import { useEffect, useState } from 'react'
import { Screen } from '@/components/layout'
import { ProgressBar } from '@/components/charts/Bars'
import { Button } from '@/components/primitives'
import { AccountRow, List } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonList } from '@/components/states'
import { api } from '@/lib/api'
import { t } from '@/lib/copy'
import { useNavigate, useRoute } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/** api.json: "Poll every 2s while status is syncing." */
const POLL_MS = 2000

/**
 * Confirms a connection and lists the accounts it brought in while the first
 * sync runs. The accounts appear immediately; the transactions land behind
 * them, so this screen is never blocked on the sync finishing.
 */
export function LinkSuccessScreen() {
  const navigate = useNavigate()
  const route = useRoute()
  const id = route.params.id

  const [tick, setTick] = useState(0)

  const connection = useAsync((signal) => api.plaid.item(id, signal), [id, tick])
  const accounts = useAsync((signal) => api.accounts.list(signal), [id])

  const item = connection.state.data
  const syncing = item?.status === 'syncing'

  // Poll only while the Server says it is still syncing.
  useEffect(() => {
    if (!syncing) return
    const timer = setTimeout(() => setTick((n) => n + 1), POLL_MS)
    return () => clearTimeout(timer)
  }, [syncing, tick])

  const linked = (accounts.state.data ?? []).filter((account) => item?.accountIds.includes(account.id))

  // Both requests gate `loading`. Keying it on the connection alone would render
  // "No accounts to add" for as long as the account list was still in flight —
  // an empty state for data that is on its way, which is not what empty means.
  const failed = connection.state.status === 'error' || accounts.state.status === 'error'

  const state = failed
    ? 'error'
    : !item || !accounts.state.data
      ? 'loading'
      : syncing
        ? 'syncing'
        : 'ready'

  return (
    <Screen
      id="linkSuccess"
      state={state}
      subtitle={item?.institutionName}
      actions={
        state === 'ready' || state === 'syncing' ? (
          <>
            <Button variant="primary" onClick={() => navigate('dashboard')}>
              {t('linkSuccess.cta.done')}
            </Button>
            <Button variant="secondary" onClick={() => navigate('accounts')}>
              {t('accounts.title')}
            </Button>
          </>
        ) : undefined
      }
    >
      {state === 'loading' ? (
        <LoadingRegion>
          <SkeletonList rows={3} />
        </LoadingRegion>
      ) : state === 'error' ? (
        <ErrorState
          titleKey="linkSuccess.error.title"
          error={
            connection.state.status === 'error'
              ? connection.state.error
              : accounts.state.status === 'error'
                ? accounts.state.error
                : undefined
          }
          onRetry={() => {
            connection.reload()
            accounts.reload()
          }}
        />
      ) : (
        <div className="stack">
          {syncing ? (
            <div className="card stack-sm">
              <span className="label">{t('linkSuccess.syncing')}</span>
              {/* The Server reports no sync percentage, so neither do we. */}
              <ProgressBar indeterminate label={t('linkSuccess.syncing')} />
            </div>
          ) : null}

          {/* The empty action is labelled for where it goes.
              `connectedAccounts.cta.add` reads "Link a bank", which would be a
              lie on a button that opens the connection list — and linkAccount is
              not an edge from linkSuccess. */}
          {linked.length === 0 ? (
            <EmptyState
              titleKey="linkSuccess.empty.title"
              actionLabel={t('settings.row.connectedAccounts')}
              onAction={() => navigate('connectedAccounts')}
            />
          ) : (
            <div className="stack-sm">
              <span className="label">{t('linkSuccess.accountsAdded', { count: linked.length })}</span>
              <List>
                {linked.map((account) => (
                  <AccountRow
                    key={account.id}
                    account={account}
                    onSelect={() => navigate('accountDetail', { id: account.id })}
                  />
                ))}
              </List>
              <Button variant="secondary" onClick={() => navigate('transactions')}>
                {t('cta.viewAll')}
              </Button>
            </div>
          )}
        </div>
      )}
    </Screen>
  )
}
